import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, ArrowRightOnRectangleIcon, LinkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import {
  fetchPlannerGoogleStatus,
  startPlannerGoogleOAuth,
  disconnectPlannerGoogle,
  listPlannerGoogleRemoteCalendars,
  createPlannerGoogleRemoteCalendar,
  plannerGoogleManualSync,
} from '../../moduleServices/planner_google_api';

/** Must match PLANNER_GOOGLE_OAUTH_MESSAGE in the backend (`postMessage` payload type). */
const PLANNER_GOOGLE_OAUTH_MESSAGE = 'quinas-planner-google-oauth';

/** Trusted HTTP origins serving the Django OAuth callback HTML (allowed `postMessage` senders). */
function trustedPlannerOAuthSenderOrigins() {
  const out = new Set();
  const push = (u) => {
    if (!u || typeof u !== 'string') return;
    try {
      const o = new URL(u.trim()).origin;
      out.add(o);
      if (o.includes('localhost')) out.add(o.replace('localhost', '127.0.0.1'));
      else if (o.includes('127.0.0.1')) out.add(o.replace('127.0.0.1', 'localhost'));
    } catch {
      /* ignore */
    }
  };
  push(process.env.REACT_APP_API_URL_LOCAL);
  push(process.env.REACT_APP_API_URL_PROD);
  return out;
}

const CardGoogleCalendar = ({ calendars = [], refreshCalendars, refreshEvents }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [awaitingOAuth, setAwaitingOAuth] = useState(false);
  const [localPick, setLocalPick] = useState('');
  const [remoteGoogleCalendars, setRemoteGoogleCalendars] = useState([]);
  const [googlePick, setGooglePick] = useState('');
  const [remoteListBusy, setRemoteListBusy] = useState(false);
  const [summary, setSummary] = useState('Quinas Planner');
  const [linkPanelOpen, setLinkPanelOpen] = useState(false);
  const connectLockRef = useRef(false);
  const finalizeOnceRef = useRef(false);
  const oauthPopupPollTimerRef = useRef(null);
  const oauthRefreshGateRef = useRef(0);
  const tryFinalizeOAuthRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const s = await fetchPlannerGoogleStatus();
      setStatus(s);
    } catch {
      setStatus({ connected: false, last_error: 'Could not load Google status.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGoogleCalendarList = useCallback(async () => {
    setRemoteListBusy(true);
    try {
      const data = await listPlannerGoogleRemoteCalendars();
      setRemoteGoogleCalendars(Array.isArray(data?.calendars) ? data.calendars : []);
      if (data?.connected === false) {
        setStatus((prev) => ({
          ...(prev || {}),
          connected: false,
          last_error: data.last_error || prev?.last_error || '',
        }));
      }
    } catch {
      toast.error('Could not load your Google calendars');
      setRemoteGoogleCalendars([]);
    } finally {
      setRemoteListBusy(false);
    }
  }, []);

  const sortedRemoteGoogleCalendars = useMemo(() => {
    const xs = [...remoteGoogleCalendars];
    xs.sort((a, b) => {
      if (a.primary && !b.primary) return -1;
      if (!a.primary && b.primary) return 1;
      return (a.summary || a.id || '').localeCompare(b.summary || b.id || '', undefined, {
        sensitivity: 'base',
      });
    });
    return xs;
  }, [remoteGoogleCalendars]);

  const clearOAuthPopupWatcher = useCallback(() => {
    if (oauthPopupPollTimerRef.current != null) {
      window.clearInterval(oauthPopupPollTimerRef.current);
      oauthPopupPollTimerRef.current = null;
    }
  }, []);

  /** Avoid double refresh when postMessage and popup close arrive almost together. */
  const syncPlannerGoogleQuiet = useCallback(async () => {
    const now = Date.now();
    if (now - oauthRefreshGateRef.current < 500) return;
    oauthRefreshGateRef.current = now;
    await reload();
    if (refreshCalendars) await refreshCalendars();
    if (refreshEvents) await refreshEvents();
  }, [reload, refreshCalendars, refreshEvents]);

  /**
   * Ensures single OAuth teardown per flow (postMessage vs popup close).
   * @param {'success' | 'failure' | 'dismissed'} reason
   */
  const tryFinalizeOAuth = useCallback(
    async (reason) => {
      if (finalizeOnceRef.current) return;
      finalizeOnceRef.current = true;
      clearOAuthPopupWatcher();
      connectLockRef.current = false;
      setAwaitingOAuth(false);
      await syncPlannerGoogleQuiet();
      if (reason === 'success') {
        toast.success('Google Calendar connected.');
      } else if (reason === 'failure') {
        toast.error('Failed to connect Google Calendar.');
      }
    },
    [clearOAuthPopupWatcher, syncPlannerGoogleQuiet],
  );

  tryFinalizeOAuthRef.current = tryFinalizeOAuth;

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (loading) return;
    if (!status?.connected) {
      setRemoteGoogleCalendars([]);
      setGooglePick('');
      setLinkPanelOpen(false);
      return;
    }
    if (!linkPanelOpen) return;
    loadGoogleCalendarList();
  }, [loading, status?.connected, linkPanelOpen, loadGoogleCalendarList]);

  useEffect(() => {
    return () => {
      clearOAuthPopupWatcher();
    };
  }, [clearOAuthPopupWatcher]);

  const applyPlannerGoogleOAuthResult = useCallback(
    (ok) => {
      void tryFinalizeOAuth(ok ? 'success' : 'failure');
    },
    [tryFinalizeOAuth],
  );

  useEffect(() => {
    const trusted = trustedPlannerOAuthSenderOrigins();
    const onMsg = (event) => {
      if (!trusted.has(event.origin)) return;
      const d = event.data;
      if (!d || d.type !== PLANNER_GOOGLE_OAUTH_MESSAGE) return;
      applyPlannerGoogleOAuthResult(!!d.ok);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [applyPlannerGoogleOAuthResult]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        reload();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  const onConnect = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (connectLockRef.current || busy) return;
    finalizeOnceRef.current = false;
    connectLockRef.current = true;
    setAwaitingOAuth(true);
    setBusy(true);
    let popupOpened = false;
    try {
      const url = await startPlannerGoogleOAuth({ popup: true });
      if (url) {
        const features =
          'popup=yes,width=560,height=720,left=80,top=80,scrollbars=yes,resizable=yes';
        const w = window.open(url, 'quinas_planner_google_oauth', features);
        if (w) {
          popupOpened = true;
          clearOAuthPopupWatcher();
          toast(
            () => (
              <span>
                A <strong>window</strong> opened for Google authentication. Close it when you are done so this
                app can refresh.
              </span>
            ),
            { duration: 9000 },
          );
          oauthPopupPollTimerRef.current = window.setInterval(() => {
            if (!w.closed) return;
            void tryFinalizeOAuthRef.current?.('dismissed');
          }, 280);
          return;
        }
        toast.error(
          'The browser blocked the window. Allow pop-ups for this site or try again.',
          { duration: 7000 },
        );
      } else {
        toast.error('Server did not return an authorization URL.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start Google connection');
    } finally {
      setBusy(false);
      if (!popupOpened) {
        connectLockRef.current = false;
        setAwaitingOAuth(false);
      }
    }
  };

  const onDisconnect = async () => {
    setBusy(true);
    try {
      await disconnectPlannerGoogle();
      toast.success('Google disconnected');
      await reload();
    } catch {
      toast.error('Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const onSyncAll = async () => {
    setBusy(true);
    try {
      const data = await plannerGoogleManualSync({});
      const n = Array.isArray(data?.summaries) ? data.summaries.length : 0;
      toast.success(`Sync finished (${n} calendar${n === 1 ? '' : 's'})`);
      refreshEvents?.();
      refreshCalendars?.();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Sync failed');
    } finally {
      setBusy(false);
    }
  };

  const onLinkOrCreateGoogle = async () => {
    if (!localPick) {
      toast.error('Choose a local calendar first');
      return;
    }
    const linkExisting = Boolean(googlePick);
    setBusy(true);
    try {
      const body = linkExisting
        ? { local_calendar_id: Number(localPick), google_calendar_id: googlePick }
        : { local_calendar_id: Number(localPick), summary };
      await createPlannerGoogleRemoteCalendar(body);
      toast.success(
        linkExisting ? 'Google calendar linked' : 'Google calendar created and linked',
      );
      await refreshCalendars?.();
      plannerGoogleManualSync({ calendar_id: localPick }).then(() => {
        toast.success('First sync queued for this calendar');
        refreshEvents?.();
      }).catch(() => {});
      await reload();
      await loadGoogleCalendarList();
      setLinkPanelOpen(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          'Could not link Google calendar',
      );
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.connected;

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Google Calendar</h3>
        {loading && <SpinnerLoad size="small" />}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Checking connection…</p>
      ) : (
        <div className="space-y-3 text-sm text-gray-700">
          {!connected ? (
            <div>
              <p className="text-gray-600 mb-2">Link your Google account to sync planner calendars.</p>
              {status?.last_error ? (
                <p className="text-xs text-red-600 break-words mb-2">{status.last_error}</p>
              ) : null}
              {awaitingOAuth ? (
                <p className="text-sm text-amber-700 mb-2">
                  Waiting on the Google window… finish there first. This button stays disabled until that
                  window closes.
                </p>
              ) : null}
              <button
                type="button"
                onClick={onConnect}
                disabled={busy || awaitingOAuth}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {awaitingOAuth ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Connect Google
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-snug">
                <span className="text-green-700 font-medium shrink-0">Connected</span>
                {status?.google_account_email ? (
                  <>
                    <span className="text-gray-400 select-none" aria-hidden>
                      ·
                    </span>
                    <span
                      className="text-gray-700 font-medium truncate max-w-[min(100vw-4rem,24rem)]"
                      title={status.google_account_email}
                    >
                      {status.google_account_email}
                    </span>
                  </>
                ) : null}
              </div>
              {status?.last_manual_sync_at ? (
                <p className="text-xs text-gray-500">
                  Last manual sync: {new Date(status.last_manual_sync_at).toLocaleString()}
                </p>
              ) : null}
              {status?.last_error ? (
                <p className="text-xs text-red-600 break-words">{status.last_error}</p>
              ) : null}

              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setLinkPanelOpen((o) => !o)}
                  disabled={busy}
                  aria-expanded={linkPanelOpen}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 text-sm"
                >
                  <LinkIcon className="h-4 w-4" aria-hidden />
                  {linkPanelOpen ? 'Cancel add' : 'Add new link'}
                </button>
                <button
                  type="button"
                  onClick={onSyncAll}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 text-sm"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Sync linked calendars
                </button>
                <button
                  type="button"
                  onClick={onDisconnect}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Disconnect
                </button>
              </div>

              <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out mt-2 ${
                  linkPanelOpen
                    ? 'grid-rows-[1fr] opacity-100'
                    : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                }`}
                aria-hidden={!linkPanelOpen}
              >
                <div className="min-h-0 overflow-hidden">
                  <div className="border-t border-gray-100 pt-3 space-y-2">
                  <p className="text-xs text-gray-500">
                    Pick planner + Google calendar, or create a new one on Google.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Local calendar</label>
                    <select
                      value={localPick}
                      onChange={(e) => setLocalPick(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                      disabled={busy || remoteListBusy}
                    >
                      <option value="">Select…</option>
                      {calendars.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <label htmlFor="planner-google-cal-pick" className="block text-xs font-medium text-gray-600">
                        Google calendar
                      </label>
                      <button
                        type="button"
                        onClick={() => loadGoogleCalendarList()}
                        disabled={busy || remoteListBusy}
                        className="p-1 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-40 flex-shrink-0"
                        aria-label="Refresh Google calendar list"
                        title="Reload list"
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${remoteListBusy ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                    <select
                      id="planner-google-cal-pick"
                      value={googlePick}
                      onChange={(e) => setGooglePick(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                      disabled={busy || remoteListBusy}
                    >
                      <option value="">Create new calendar…</option>
                      {sortedRemoteGoogleCalendars.map((c) => (
                        <option key={c.id} value={c.id} title={c.id}>
                          {c.summary || c.id || 'Untitled'}
                          {c.primary ? ' (primary)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!googlePick ? (
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-medium text-gray-600 mb-1">New calendar title</label>
                      <input
                        type="text"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                        disabled={busy || remoteListBusy}
                        placeholder="Quinas Planner"
                      />
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={onLinkOrCreateGoogle}
                    disabled={busy || remoteListBusy}
                    className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 text-sm whitespace-nowrap sm:self-end"
                  >
                    {googlePick ? 'Link' : 'Create & link'}
                  </button>
                </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CardGoogleCalendar;
